import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/content";
import { media } from "@/content/media";
import { Section, Photo } from "@/components/Layout";
import { WinnerSeal } from "@/components/Brand";
import { Reveal, SectionHead, GoldLine } from "@/components/Motion";

export const GrandPrix = () => {
  const g = useContent().categories.grandPrix;
  return (
    <Reveal className="tone-plum relative mt-20 overflow-hidden rounded-[5px] bg-plum text-white" data-testid="category-15-grandprix">
      <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
        <div className="relative flex flex-col justify-center p-8 md:p-16">
          <div className="flex items-end gap-6">
            <span className="font-serif text-7xl font-extralight leading-none text-copper md:text-8xl">{g.n}</span>
            <span className="mb-3 text-[0.75rem] font-semibold uppercase tracking-[0.45em] text-copper">{g.label}</span>
          </div>
          <GoldLine className="my-8 max-w-[160px]" />
          <h3 className="font-serif text-2xl leading-[1.2] md:text-[1.75rem]">{g.title}</h3>
          <p className="mt-6 max-w-xl leading-relaxed text-white/80">{g.text}</p>
          <div className="mt-10"><WinnerSeal size={120} /></div>
        </div>
        <Photo src={media.grandPrix} alt={g.imageAlt} className="min-h-[340px] rounded-none lg:min-h-[520px]" />
      </div>
    </Reveal>
  );
};

export const CategoriesList = () => {
  const c = useContent().categories;
  return (
    <>
      <ul className="grid border-t border-brown/20 md:grid-cols-2" data-testid="categories-grid">
        {c.items.map((it, i) => (
          <Reveal as="li" key={it.n} delay={(i % 2) * 0.1} className="group relative border-b border-brown/20 md:odd:border-e" data-testid={`category-${it.n}`}>
            <div className="flex gap-4 p-5 transition-colors duration-700 group-hover:bg-plum/[0.05] md:gap-9 md:p-10">
              <span className="font-serif text-4xl font-extralight leading-[0.9] text-copper tabular-nums w-[3.4rem] shrink-0 transition-transform duration-700 group-hover:-translate-y-1 md:w-[5.4rem] md:text-7xl">{it.n}</span>
              <div>
                <h3 className="font-serif text-[0.9rem] font-medium uppercase leading-relaxed tracking-[0.12em] text-plum">{it.title}</h3>
                <p className="mt-4 text-[0.97rem] leading-relaxed text-brown/90">{it.text}</p>
              </div>
            </div>
            <span aria-hidden="true" className="absolute bottom-0 start-0 h-px w-full origin-left scale-x-0 bg-copper transition-transform duration-700 group-hover:scale-x-100" />
          </Reveal>
        ))}
      </ul>
      <GrandPrix />
      <Reveal className="mt-10"><p className="max-w-3xl text-sm leading-relaxed text-brown/90">{c.nb}</p></Reveal>
    </>
  );
};

export const Categories = () => {
  const c = useContent().categories;
  const seeAll = useContent().ui.seeAll;
  return (
    <Section id={c.id} tone="night" className="py-20 md:py-28">
      <div className="mb-16 flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <SectionHead eyebrow={c.eyebrow} title={c.title} tone="white" testid="categories-title" />
        <Reveal>
          <Link to="/categories" data-testid="categories-see-all" className="link-line inline-flex items-center gap-2 text-[0.75rem] font-semibold uppercase tracking-[0.3em] text-copper">{seeAll} <ArrowUpRight size={14} strokeWidth={1.4} /></Link>
        </Reveal>
      </div>
      <CategoriesList />
    </Section>
  );
};
